import 'css/src/components/public-pages/contentful-components/AudioSyncUtilityComponent';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import AudioTrackTimeline from './audio-sync/AudioTrackTimeline';
import { useMediaBunnyMixer } from './audio-sync/useMediaBunnyMixer';

import { getTimeStringFromMillis } from 'js/src/components/common/asset-trimmer/common';
import { Toast } from 'js/src/components/common/Toast';
import {
    BUTTON_SIZES,
    BUTTON_WIDTHS,
    PrimaryButton,
    SimpleButton,
    SPINNER_ICON,
    TertiaryButton,
} from 'js/src/components/common/ui/Button';
import Checkbox from 'js/src/components/common/ui/Checkbox';

const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
const ACCEPTED_AUDIO_TYPES = [
    'audio/mpeg',
    'audio/wav',
    'audio/ogg',
    'audio/aac',
    'audio/mp3',
    'audio/x-wav',
];

interface AudioSyncUtilityComponentProps {
    bgColor: string;
}

const AudioSyncUtilityComponent: React.FC<AudioSyncUtilityComponentProps> = ({ bgColor }) => {
    const {
        state,
        setVideoFile,
        addAudioTrack,
        removeAudioTrack,
        updateAudioTrackOffset,
        setIncludeVideoAudio,
        generatePreview,
        downloadVideo,
        reset,
    } = useMediaBunnyMixer();

    const videoInputRef = useRef<HTMLInputElement>(null);
    const audioInputRef = useRef<HTMLInputElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const previewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [isVideoDragOver, setIsVideoDragOver] = useState(false);
    const [currentTimeMs, setCurrentTimeMs] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1);

    // Calculate minimum zoom based on content duration
    // The longest content should take up at least 75% of the timeline width
    const minZoom = useMemo(() => {
        const effectiveVideoDuration = state.videoDurationMs > 0 ? state.videoDurationMs : 30000;

        // Find the total content span (including negative offsets and extended audio)
        let minOffset = 0;
        let maxEnd = effectiveVideoDuration;

        state.audioTracks.forEach((track) => {
            if (track.offsetMs < minOffset) {
                minOffset = track.offsetMs;
            }
            const trackEnd = track.offsetMs + track.durationMs;
            if (trackEnd > maxEnd) {
                maxEnd = trackEnd;
            }
        });

        const totalDurationMs = maxEnd - Math.min(minOffset, 0);

        // At minZoom, the total content should fill 75% of the visible area
        // visibleDuration at zoom Z = effectiveVideoDuration / Z
        // We want: totalDurationMs = 0.75 * (effectiveVideoDuration / minZoom)
        // So: minZoom = 0.75 * effectiveVideoDuration / totalDurationMs
        const calculatedMin = (0.75 * effectiveVideoDuration) / totalDurationMs;

        // Don't allow zoom smaller than what would show everything at 75%
        return Math.max(0.1, calculatedMin); // Floor at 10% to prevent extreme zoom out
    }, [state.videoDurationMs, state.audioTracks]);

    const handleZoomIn = useCallback(() => {
        setZoomLevel((prev) => Math.min(prev * 1.5, 10)); // Max 10x zoom
    }, []);

    const handleZoomOut = useCallback(() => {
        setZoomLevel((prev) => Math.max(prev / 1.5, minZoom));
    }, [minZoom]);

    // Auto-generate preview when video, audio tracks, offsets, or includeVideoAudio changes
    useEffect(() => {
        // Clear any pending debounce
        if (previewDebounceRef.current) {
            clearTimeout(previewDebounceRef.current);
        }

        // Only generate if we have a video and either audio tracks or includeVideoAudio
        if (
            state.videoFile &&
            state.videoDurationMs > 0 &&
            (state.audioTracks.length > 0 || state.includeVideoAudio) &&
            !state.isProcessing
        ) {
            // Debounce to avoid regenerating on every tiny drag movement
            previewDebounceRef.current = setTimeout(() => {
                generatePreview();
            }, 500);
        }

        return () => {
            if (previewDebounceRef.current) {
                clearTimeout(previewDebounceRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.videoFile, state.videoDurationMs, state.audioTracks, state.includeVideoAudio]);

    const handleVideoChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (file) {
                setVideoFile(file);
            }
            // Reset input so the same file can be selected again
            if (videoInputRef.current) {
                videoInputRef.current.value = '';
            }
        },
        [setVideoFile],
    );

    const handleAudioChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = e.target.files;
            if (files) {
                for (const file of Array.from(files)) {
                    await addAudioTrack(file);
                }
            }
            // Reset input so the same file can be selected again
            if (audioInputRef.current) {
                audioInputRef.current.value = '';
            }
        },
        [addAudioTrack],
    );

    const handleVideoUploadClick = useCallback(() => {
        videoInputRef.current?.click();
    }, []);

    const handleAudioUploadClick = useCallback(() => {
        audioInputRef.current?.click();
    }, []);

    const handleClearVideo = useCallback(() => {
        setVideoFile(null);
    }, [setVideoFile]);

    const handleIncludeVideoAudioChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setIncludeVideoAudio(e.target.checked);
        },
        [setIncludeVideoAudio],
    );

    const handleVideoDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsVideoDragOver(true);
    }, []);

    const handleVideoDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsVideoDragOver(false);
    }, []);

    const handleVideoDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsVideoDragOver(false);

            const files = e.dataTransfer.files;
            if (files.length > 0) {
                const file = files[0];
                // Check if it's a video file
                if (ACCEPTED_VIDEO_TYPES.includes(file.type) || file.type.startsWith('video/')) {
                    setVideoFile(file);
                } else {
                    Toast.error('Unsupported file. Please upload a video file.');
                }
            }
        },
        [setVideoFile],
    );

    const handleAudioDrop = useCallback(
        async (files: FileList) => {
            for (const file of Array.from(files)) {
                // Check if it's an audio file
                if (ACCEPTED_AUDIO_TYPES.includes(file.type) || file.type.startsWith('audio/')) {
                    await addAudioTrack(file);
                } else {
                    Toast.error('Unsupported file. Please upload an audio file.');
                }
            }
        },
        [addAudioTrack],
    );

    const handlePlay = useCallback(() => {
        setIsPlaying(true);
    }, []);

    const handlePause = useCallback(() => {
        setIsPlaying(false);
    }, []);

    const handleTimeUpdate = useCallback(() => {
        if (videoRef.current && !isPlaying) {
            setCurrentTimeMs(videoRef.current.currentTime * 1000);
        }
    }, [isPlaying]);

    useEffect(() => {
        if (!isPlaying || !videoRef.current) return;

        let animationFrameId: number;

        const updatePlayhead = () => {
            if (videoRef.current) {
                setCurrentTimeMs(videoRef.current.currentTime * 1000);
            }
            animationFrameId = requestAnimationFrame(updatePlayhead);
        };

        animationFrameId = requestAnimationFrame(updatePlayhead);

        return () => {
            cancelAnimationFrame(animationFrameId);
        };
    }, [isPlaying]);

    const handleEnded = useCallback(() => {
        setIsPlaying(false);
        setCurrentTimeMs(0);
    }, []);

    const handleSeek = useCallback((timeMs: number) => {
        if (videoRef.current) {
            videoRef.current.currentTime = timeMs / 1000;
            setCurrentTimeMs(timeMs);
        }
    }, []);

    const handleTogglePlay = useCallback(() => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
        }
    }, [isPlaying]);

    return (
        <div
            className="audio-sync-utility contentful-component"
            style={{ backgroundColor: bgColor }}
        >
            <div className="container">
                <input
                    ref={videoInputRef}
                    type="file"
                    accept={ACCEPTED_VIDEO_TYPES.join(',')}
                    onChange={handleVideoChange}
                    className="d-none"
                    data-testid="video-file-input"
                />
                <input
                    ref={audioInputRef}
                    type="file"
                    accept={ACCEPTED_AUDIO_TYPES.join(',')}
                    multiple
                    onChange={handleAudioChange}
                    className="d-none"
                    data-testid="audio-file-input"
                />

                <div className="audio-sync-preview-section">
                    <div
                        className={`preview-container position-relative d-flex align-items-center justify-content-center ${isVideoDragOver ? 'drag-over' : ''}`}
                        onDragOver={handleVideoDragOver}
                        onDragLeave={handleVideoDragLeave}
                        onDrop={handleVideoDrop}
                    >
                        {state.isProcessing && (
                            <div className="preview-processing-overlay position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center">
                                <div className="spinner" />
                                <span>Generating preview...</span>
                            </div>
                        )}
                        {isVideoDragOver && (
                            <div className="preview-drop-overlay position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center">
                                <i className="material-symbols-outlined drop-icon">upload_file</i>
                                <span>Drop video file here</span>
                            </div>
                        )}
                        {state.previewUrl ? (
                            <video
                                ref={videoRef}
                                className="preview-video w-100 h-100"
                                src={state.previewUrl}
                                playsInline
                                onClick={handleTogglePlay}
                                onTimeUpdate={handleTimeUpdate}
                                onPlay={handlePlay}
                                onPause={handlePause}
                                onEnded={handleEnded}
                            />
                        ) : state.videoPreviewUrl ? (
                            <video
                                ref={videoRef}
                                className="preview-video w-100 h-100"
                                src={state.videoPreviewUrl}
                                playsInline
                                muted
                                onClick={handleTogglePlay}
                                onTimeUpdate={handleTimeUpdate}
                                onPlay={handlePlay}
                                onPause={handlePause}
                                onEnded={handleEnded}
                            />
                        ) : (
                            <button
                                className="preview-empty-upload w-100 h-100 d-flex flex-column align-items-center justify-content-center"
                                onClick={handleVideoUploadClick}
                                type="button"
                            >
                                <i className="material-symbols-outlined upload-icon color-white">
                                    movie
                                </i>
                                <span className="upload-text">Click or drag video file here</span>
                                <span className="upload-hint">MP4, MOV, WebM supported</span>
                            </button>
                        )}
                        {(state.previewUrl || state.videoPreviewUrl) && (
                            <div
                                className={`play-pause-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center ${isPlaying ? 'playing' : 'paused'}`}
                                onClick={handleTogglePlay}
                            >
                                <i className="material-symbols-outlined material-symbols-f1 play-pause-icon d-flex align-items-center justify-content-center">
                                    {isPlaying ? 'pause' : 'play_arrow'}
                                </i>
                            </div>
                        )}
                    </div>
                </div>

                {state.videoFile && (
                    <div className="playback-controls position-relative d-flex align-items-center justify-content-end py-l5-12">
                        <div className="playback-controls-center position-absolute start-50 translate-middle-x d-flex align-items-center">
                            <SimpleButton
                                onClick={handleTogglePlay}
                                width={BUTTON_WIDTHS.NO_MIN_WIDTH}
                                className="material-symbols-f1"
                                iconLeft={isPlaying ? 'pause' : 'play_arrow'}
                            />
                            <span className="current-time-display">
                                {getTimeStringFromMillis(currentTimeMs)} /{' '}
                                {getTimeStringFromMillis(state.videoDurationMs)}
                            </span>
                        </div>
                        <div className="zoom-controls d-flex align-items-center">
                            <i className="material-symbols-outlined zoom-icon">search</i>
                            <button
                                className="zoom-btn d-flex align-items-center justify-content-center"
                                onClick={handleZoomOut}
                                type="button"
                                title="Zoom out"
                                disabled={zoomLevel <= minZoom}
                            >
                                <i className="material-symbols-outlined">remove</i>
                            </button>
                            <span className="zoom-level">{Math.round(zoomLevel * 100)}%</span>
                            <button
                                className="zoom-btn"
                                onClick={handleZoomIn}
                                type="button"
                                title="Zoom in"
                                disabled={zoomLevel >= 10}
                            >
                                <i className="material-symbols-outlined">add</i>
                            </button>
                        </div>
                    </div>
                )}

                <div className="audio-sync-timeline-section">
                    <AudioTrackTimeline
                        videoDurationMs={state.videoDurationMs}
                        videoFile={state.videoFile}
                        audioTracks={state.audioTracks}
                        currentTimeMs={currentTimeMs}
                        isPlaying={isPlaying}
                        zoomLevel={zoomLevel}
                        onAudioTrackOffsetChange={updateAudioTrackOffset}
                        onAudioTrackRemove={removeAudioTrack}
                        onVideoUploadClick={handleVideoUploadClick}
                        onVideoClear={handleClearVideo}
                        onAudioUploadClick={handleAudioUploadClick}
                        onAudioDrop={handleAudioDrop}
                        onSeek={handleSeek}
                    />
                </div>

                <div className="audio-sync-actions d-flex flex-wrap justify-content-center mt-l5-24">
                    <PrimaryButton
                        onClick={downloadVideo}
                        disabled={state.isProcessing || !state.videoFile}
                        text={state.isProcessing ? 'Processing...' : 'Download'}
                        iconLeft={state.isProcessing ? SPINNER_ICON : 'download'}
                        size={BUTTON_SIZES.LG}
                    />

                    <TertiaryButton
                        onClick={reset}
                        disabled={state.isProcessing}
                        text="Reset"
                        iconLeft="refresh"
                        size={BUTTON_SIZES.LG}
                    />
                </div>

                <div className="audio-sync-options d-flex justify-content-center mt-l5-16 py-l5-16">
                    <Checkbox
                        checked={state.includeVideoAudio}
                        onChange={handleIncludeVideoAudioChange}
                        disabled={!state.videoFile}
                        text="Include original audio from video"
                    />
                </div>
            </div>
        </div>
    );
};

export default React.memo(AudioSyncUtilityComponent);
